import React, { useEffect, useState } from "react";
import { decodeHtmlEntities } from "@/lib/utils";
import type { EcommerceProduct } from "@/integrations/wordpress/types";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare global {
  const wvcClient: any;
}

/**
 * Extended image record holding URL and Media Library alt text
 */
export interface ProductImageWithAlt {
  url: string;
  alt: string;
}

export interface ProductAltRecord {
  featuredAlt?: string;
  images: ProductImageWithAlt[];
}

/**
 * Global in-memory cache mapping productId -> ProductAltRecord
 */
const altCache = new Map<string, ProductAltRecord>();

/**
 * Listener set for reactive updates when alt data finishes loading.
 */
const listeners = new Set<() => void>();

function notifyListeners() {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // ignore
    }
  });
}

/**
 * Strip size suffix like -300x300, -150x150, -768x512, etc., from filename or url
 * to allow tolerant matching between thumbnail / full image URLs.
 */
function normalizeImageUrlForComparison(url: string): string {
  if (!url) return "";
  try {
    const parsed = new URL(url, "http://localhost");
    const pathname = parsed.pathname;
    const filename = pathname.split("/").pop() || "";
    const dotIndex = filename.lastIndexOf(".");
    const baseName = dotIndex !== -1 ? filename.slice(0, dotIndex) : filename;
    const cleanBase = baseName.replace(/-\d+x\d+$/i, "");
    return cleanBase.toLowerCase();
  } catch {
    const filename = url.split("/").pop() || "";
    const dotIndex = filename.lastIndexOf(".");
    const baseName = dotIndex !== -1 ? filename.slice(0, dotIndex) : filename;
    return baseName.replace(/-\d+x\d+$/i, "").toLowerCase();
  }
}

/**
 * Ingest raw WordPress product / variation objects and populate altCache.
 */
export function ingestRawProductAlts(rawProducts: any[]): void {
  if (!Array.isArray(rawProducts)) return;
  let updated = false;

  for (const raw of rawProducts) {
    if (!raw || typeof raw !== "object") continue;
    const id = String(raw.id ?? raw.ID ?? "");
    if (!id) continue;

    const imagesMap: ProductImageWithAlt[] = [];
    const addImageRecord = (url: unknown, alt: unknown) => {
      if (typeof url !== "string" || !url.trim()) return;
      const cleanUrl = url.trim();
      const cleanAlt = typeof alt === "string" ? decodeHtmlEntities(alt).trim() : "";
      if (!imagesMap.some((img) => img.url === cleanUrl)) {
        imagesMap.push({ url: cleanUrl, alt: cleanAlt });
      }
    };

    // Store API images array: [{ id, src, alt, name }, ...]
    const rawImages = Array.isArray(raw.images)
      ? raw.images
      : Array.isArray(raw.raw?.images)
        ? raw.raw.images
        : [];
    rawImages.forEach((img: any) => {
      if (img && typeof img === "object") {
        addImageRecord(img.src || img.url, img.alt || img.alt_text);
      }
    });

    // Embedded media
    const embeddedMedia =
      raw._embedded?.["wp:featuredmedia"] ||
      raw.raw?._embedded?.["wp:featuredmedia"];
    if (Array.isArray(embeddedMedia)) {
      embeddedMedia.forEach((media: any) => {
        if (media && typeof media === "object") {
          addImageRecord(media.source_url, media.alt_text || media.alt);
        }
      });
    }

    // Direct gallery_images if already present
    if (Array.isArray(raw.gallery_images)) {
      raw.gallery_images.forEach((img: any) => {
        if (img && typeof img === "object") {
          addImageRecord(img.url || img.src, img.alt || img.alt_text);
        }
      });
    }

    // Featured alt
    let featuredAlt =
      (typeof raw.featured_image_alt === "string" && raw.featured_image_alt.trim()) ||
      (typeof raw.featured_media_alt === "string" && raw.featured_media_alt.trim()) ||
      (typeof raw.raw?.featured_image_alt === "string" && raw.raw.featured_image_alt.trim()) ||
      (typeof raw.raw?.featured_media_alt === "string" && raw.raw.featured_media_alt.trim()) ||
      (Array.isArray(embeddedMedia) && typeof embeddedMedia[0]?.alt_text === "string" && embeddedMedia[0].alt_text.trim()) ||
      (rawImages[0] && typeof rawImages[0].alt === "string" && rawImages[0].alt.trim()) ||
      undefined;

    if (featuredAlt) {
      featuredAlt = decodeHtmlEntities(featuredAlt).trim();
    }

    // Only update if we have meaningful data or cache doesn't have it
    const existing = altCache.get(id);
    const hasNewData = Boolean(featuredAlt) || imagesMap.some((img) => Boolean(img.alt));
    if (!existing || hasNewData) {
      altCache.set(id, { featuredAlt: featuredAlt || existing?.featuredAlt, images: imagesMap.length > 0 ? imagesMap : existing?.images || [] });
      updated = true;
    }
  }

  if (updated) {
    notifyListeners();
  }
}

let isFetchingCatalogAlts = false;
let catalogAltsFetched = false;

/**
 * Fetch raw products with embeds to populate the alt text cache.
 */
export async function loadCatalogProductAlts(): Promise<void> {
  if (catalogAltsFetched || isFetchingCatalogAlts) return;
  if (typeof wvcClient === "undefined") return;

  isFetchingCatalogAlts = true;

  try {
    const queryVars: Record<string, any> = {
      post_type: "product",
      per_page: 100,
    };
    const wpQuery = new WP_Query(queryVars);
    wpQuery.parse_query_vars();

    let rawPosts: any[] = [];
    if (typeof wvcClient.get_wp_query_results === "function") {
      const res = await wvcClient.get_wp_query_results(
        wpQuery.toJSON(),
        {
          embeds: ["wp:featuredmedia"],
          fields: [],
        },
        false
      );
      rawPosts = res?.posts ?? [];
    } else if (typeof wvcClient.getStoreProducts === "function") {
      const res = await wvcClient.getStoreProducts(queryVars);
      rawPosts = Array.isArray(res) ? res : res?.products ?? [];
    }

    if (rawPosts.length > 0) {
      ingestRawProductAlts(rawPosts);
      catalogAltsFetched = true;
    }
  } catch {
    // Ignore fetch errors; getProductImageAlt falls back to title
  } finally {
    isFetchingCatalogAlts = false;
  }
}

/**
 * Fetch raw variation alts for a specific parent product id or variation list.
 */
export async function loadVariationAlts(parentId: string | number, variationIds?: Array<string | number>): Promise<void> {
  if (typeof wvcClient === "undefined" || !parentId) return;

  try {
    const parent = Number(parentId);
    const queryVars: Record<string, any> = {
      type: "variation",
      parent,
      post_parent: parent,
      per_page: 100,
      post_type: "product",
    };
    if (Array.isArray(variationIds) && variationIds.length > 0) {
      queryVars.include = variationIds.map(Number).filter((id) => !Number.isNaN(id));
    }

    const wpQuery = new WP_Query(queryVars);
    wpQuery.parse_query_vars();

    const storeApiParams = {
      type: "variation",
      parent,
    };

    let rawPosts: any[] = [];
    if (typeof wvcClient.get_wp_query_results === "function") {
      const res = await wvcClient.get_wp_query_results(
        wpQuery.toJSON(),
        storeApiParams,
        false
      );
      rawPosts = res?.posts ?? [];
    } else if (typeof wvcClient.getStoreProducts === "function") {
      const res = await wvcClient.getStoreProducts(queryVars);
      rawPosts = Array.isArray(res) ? res : res?.products ?? [];
    }

    if (rawPosts.length > 0) {
      ingestRawProductAlts(rawPosts);
    }
  } catch {
    // Ignore fetch errors
  }
}

/**
 * React hook that triggers loading raw product image alt text and re-renders when available.
 */
export function useProductImageAlts(productId?: string | number): void {
  const [, setTick] = useState(0);

  useEffect(() => {
    const handleChange = () => setTick((prev) => prev + 1);
    listeners.add(handleChange);

    // Trigger catalog fetch if not already done
    void loadCatalogProductAlts();

    // If specific product id (e.g. variable product) is passed, fetch its variations as well
    if (productId) {
      void loadVariationAlts(productId);
    }

    return () => {
      listeners.delete(handleChange);
    };
  }, [productId]);
}

/**
 * Extract Media Library alt text from raw product data or custom fields.
 * Also checks the module-level altCache.
 */
export function extractProductAltSources(product: any): {
  featuredAlt?: string;
  images: ProductImageWithAlt[];
} {
  if (!product || typeof product !== "object") {
    return { images: [] };
  }

  const imagesMap: ProductImageWithAlt[] = [];
  const addImageRecord = (url: unknown, alt: unknown) => {
    if (typeof url !== "string" || !url.trim()) return;
    const cleanUrl = url.trim();
    const cleanAlt = typeof alt === "string" ? decodeHtmlEntities(alt).trim() : "";
    if (!imagesMap.some((img) => img.url === cleanUrl)) {
      imagesMap.push({ url: cleanUrl, alt: cleanAlt });
    }
  };

  // 1. Direct fields on product (e.g. if gallery_images exists)
  if (Array.isArray(product.gallery_images)) {
    product.gallery_images.forEach((img: any) => {
      if (img && typeof img === "object") {
        addImageRecord(img.url || img.src, img.alt || img.alt_text);
      }
    });
  }

  // 2. Store API raw.images array
  const rawImages = Array.isArray(product.images)
    ? product.images
    : Array.isArray(product.raw?.images)
      ? product.raw.images
      : [];
  rawImages.forEach((img: any) => {
    if (img && typeof img === "object") {
      addImageRecord(img.src || img.url, img.alt || img.alt_text);
    }
  });

  // 3. Embedded media
  const embeddedMedia =
    product._embedded?.["wp:featuredmedia"] ||
    product.raw?._embedded?.["wp:featuredmedia"];
  if (Array.isArray(embeddedMedia)) {
    embeddedMedia.forEach((media: any) => {
      if (media && typeof media === "object") {
        addImageRecord(media.source_url, media.alt_text || media.alt);
      }
    });
  }

  // 4. Check cached alt record by product ID
  const productId = String(product.id ?? product.ID ?? "");
  const cached = productId ? altCache.get(productId) : undefined;
  if (cached) {
    cached.images.forEach((img) => {
      addImageRecord(img.url, img.alt);
    });
  }

  // Featured alt fallback candidates
  let featuredAlt =
    (typeof product.featured_image_alt === "string" && product.featured_image_alt.trim()) ||
    (typeof product.featured_media_alt === "string" && product.featured_media_alt.trim()) ||
    (typeof product.raw?.featured_image_alt === "string" && product.raw.featured_image_alt.trim()) ||
    (typeof product.raw?.featured_media_alt === "string" && product.raw.featured_media_alt.trim()) ||
    (Array.isArray(embeddedMedia) && typeof embeddedMedia[0]?.alt_text === "string" && embeddedMedia[0].alt_text.trim()) ||
    (rawImages[0] && typeof rawImages[0].alt === "string" && rawImages[0].alt.trim()) ||
    cached?.featuredAlt ||
    undefined;

  if (featuredAlt) {
    featuredAlt = decodeHtmlEntities(featuredAlt).trim();
  }

  return { featuredAlt, images: imagesMap };
}

export function resolveProductMaxQuantity(item: any): number | undefined {
  if (!item || typeof item !== "object") return undefined;

  const limits: number[] = [];

  const parseNumber = (val: unknown): number | undefined => {
    if (typeof val === "number" && Number.isFinite(val)) return val;
    if (typeof val === "string" && val.trim() !== "") {
      const num = Number(val);
      if (Number.isFinite(num)) return num;
    }
    return undefined;
  };

  // 1. low_stock_remaining
  const lowStock = parseNumber(item.low_stock_remaining ?? item.raw?.low_stock_remaining);
  if (lowStock !== undefined && lowStock > 0) {
    limits.push(lowStock);
  }

  // 2. add_to_cart.maximum
  const addToCartMax = parseNumber(
    item.add_to_cart?.maximum ??
    item.raw?.add_to_cart?.maximum ??
    item.add_to_cart?.max_quantity ??
    item.raw?.add_to_cart?.max_quantity
  );
  if (addToCartMax !== undefined && addToCartMax > 0) {
    limits.push(addToCartMax);
  }

  // 3. stock_quantity (when manage_stock is true or stock_quantity is specified)
  const stockQuantity = parseNumber(item.stock_quantity ?? item.raw?.stock_quantity);
  if (stockQuantity !== undefined && stockQuantity >= 0) {
    limits.push(stockQuantity);
  }

  // 4. max_purchase_quantity
  const maxPurchase = parseNumber(item.max_purchase_quantity ?? item.raw?.max_purchase_quantity);
  if (maxPurchase !== undefined && maxPurchase > 0) {
    limits.push(maxPurchase);
  }

  if (limits.length === 0) return undefined;
  return Math.min(...limits);
}

/**
 * Returns the WordPress Media Library alt text for a given product image URL.
 * Tolerantly matches URLs (exact match first, then by filename without size suffix).
 * Falls back to featured_image_alt if applicable, and finally product.title.
 */
export function getProductImageAlt(
  product: EcommerceProduct | any,
  imageUrl?: string | null
): string {
  const fallbackTitle = String(product?.title || product?.name || "").trim() || "Research Product";
  if (!product) {
    return fallbackTitle;
  }

  // Auto-trigger background load if cache is empty
  if (!catalogAltsFetched && !isFetchingCatalogAlts && typeof window !== "undefined") {
    void loadCatalogProductAlts();
  }

  const { featuredAlt, images } = extractProductAltSources(product);
  const targetUrl = imageUrl ? imageUrl.trim() : "";

  // If no URL given, try featuredAlt, then first image alt, then fallbackTitle
  if (!targetUrl) {
    if (featuredAlt) return featuredAlt;
    if (images.length > 0 && images[0].alt) return images[0].alt;
    return fallbackTitle;
  }

  // 1. Exact match in gallery/images
  const exact = images.find((img) => img.url === targetUrl);
  if (exact && exact.alt) {
    return exact.alt;
  }

  // 2. Tolerant match (comparing normalized filenames without size suffixes)
  const targetNorm = normalizeImageUrlForComparison(targetUrl);
  if (targetNorm) {
    const tolerant = images.find((img) => normalizeImageUrlForComparison(img.url) === targetNorm);
    if (tolerant && tolerant.alt) {
      return tolerant.alt;
    }
  }

  // Also check cache across all cached records if targetNorm matches any known image URL
  for (const record of altCache.values()) {
    const match = record.images.find(
      (img) => img.url === targetUrl || (targetNorm && normalizeImageUrlForComparison(img.url) === targetNorm)
    );
    if (match && match.alt) {
      return match.alt;
    }
    if (record.featuredAlt && targetNorm && record.images[0] && normalizeImageUrlForComparison(record.images[0].url) === targetNorm) {
      return record.featuredAlt;
    }
  }

  // 3. If targetUrl matches product's featured image or no other alt found
  const featuredNorm = normalizeImageUrlForComparison(product.featured_image_url || "");
  if (featuredAlt && (targetUrl === product.featured_image_url || (targetNorm && targetNorm === featuredNorm))) {
    return featuredAlt;
  }

  // 4. If we have a featuredAlt and target is the first gallery image
  if (featuredAlt && product.gallery_image_urls?.[0] === targetUrl) {
    return featuredAlt;
  }

  // 5. Default fallback to product title
  return fallbackTitle;
}
